import React, { useLayoutEffect } from 'react';
import type { LanguageCode } from '../types/types';
import './TribalBorder.css';

// Bandeau sous l'en-tête.
// Bambara, peul et wolof : façade « Porte de Ségou » (banco, pilastres,
// torons, porte-tour à pinacles où se tient l'ibis). Copte, lingala et
// swahili : une porte de leur région (Tour 15 · 15b, 15e, 15f). Kama :
// pylône de Karnak (Tour 16 · 16a). Le choix se fait en CSS via <html data-lang>.
const SIDE_UNITS = 8; // pilastre + pan de mur ; le CSS en masque selon la largeur

const Wall: React.FC = () => (
  <>
    <span className="segou__pilaster" />
    <span className="segou__wall"><span className="segou__window" /></span>
  </>
);

// Porte monumentale « Voûte étagée » (maquette 11a) : deux tours à torons,
// façade crénelée, frise de triangles, voûte en trois gradins avec clé en losange.
const MERLONS  = Array.from({ length: 8 }, (_, i) => 26 + i * 7.8);
const TRIANGLES = Array.from({ length: 7 }, (_, i) => 34 + i * 7.6);
const TORONS_Y = [30, 52, 74];

// ibis : l'ibis sur le seuil (bandeau) ; le sélecteur de langue l'omet.
type GateProps = { ibis?: boolean };

const SegouGate: React.FC<GateProps> = ({ ibis = true }) => (
  <svg className="segou__gate" width="120" height="96" viewBox="0 0 120 96">
    {/* Tours */}
    <path d="M5 96 L6.5 24 Q7 11 15.5 9.5 Q24.5 10.5 25 23 L26.5 96 Z" fill="#A85F33" />
    <path d="M93.5 96 L95 23 Q95.5 10.5 104.5 9.5 Q113 11 113.5 24 L115 96 Z" fill="#A85F33" />
    {TORONS_Y.map(y => (
      <React.Fragment key={y}>
        <rect x="2" y={y} width="28" height="2.4" rx="1.2" fill="#3D2314" />
        <rect x="90" y={y} width="28" height="2.4" rx="1.2" fill="#3D2314" />
      </React.Fragment>
    ))}
    {/* Façade crénelée */}
    <path d="M24 96 L25 33 L95 33 L96 96 Z" fill="#A85F33" />
    {MERLONS.map(x => (
      <path key={x} d={`M${x} 33 L${x} 28.8 Q${x + 2.8} 25.4 ${x + 5.6} 28.8 L${x + 5.6} 33 Z`} fill="#A85F33" />
    ))}
    <rect x="24" y="32" width="72" height="3.2" fill="#8A4A25" />
    <path d="M26 35.2 L94 35.2 L94 37 L26 37 Z" fill="#00000026" />
    {/* Frise de triangles bambara sous la corniche */}
    {TRIANGLES.map(x => (
      <path key={x} d={`M${x} 39 L${x + 5.32} 39 L${x + 2.66} 43.6 Z`} fill="#8A4A25" />
    ))}
    {/* Niches et fenêtres */}
    <path d="M28 60 L31.5 53 L35 60 Z" fill="#241812" />
    <path d="M85 60 L88.5 53 L92 60 Z" fill="#241812" />
    <path d="M28.5 72 L34.5 72 L34.5 80 L28.5 80 Z" fill="#241812" />
    <path d="M85.5 72 L91.5 72 L91.5 80 L85.5 80 Z" fill="#241812" />
    {/* Voûte en trois gradins */}
    <path d="M37.5 96 L37.5 62 Q38 45 61 44.5 Q84 45 84.5 62 L84.5 96 Z" fill="#00000030" transform="translate(2.2 1.6)" />
    <path d="M36 96 L36 62 Q36.5 45 60 44.5 Q83.5 45 84 62 L84 96 Z" fill="#D4925F" />
    <path d="M40.5 96 L40.5 64 Q41 50 60 49.5 Q79 50 79.5 64 L79.5 96 Z" fill="#8A4A25" />
    <path d="M58 44 L60 41 L62 44 L60 47 Z" fill="#3D2314" />
    <path d="M45 96 L45 66 Q45.5 55.5 60 55 Q74.5 55.5 75 66 L75 96 Z" fill="#241812" />
    <path d="M45 96 L45 66 Q45.5 55.5 60 55 L60 59.5 Q50 60.5 49.5 69 L49.5 96 Z" fill="#000000" opacity="0.38" />
    {/* L'ibis sur le seuil */}
    {ibis && <image className="segou__ibis" href="/Mascotte/mascotte5.png" x="50" y="67" width="20" height="27" preserveAspectRatio="xMidYMax meet" />}
    <rect x="24" y="93" width="72" height="3" fill="#8A4A25" />
  </svg>
);

// ===================================================================
// Une porte par langue (maquette Tour 15) — même construction que 11a :
// porte SVG 120 × 96 au centre, murs latéraux, entrée sombre, ombre portée.
// ===================================================================

// L'ibis sur le seuil, comme à Ségou (masqué si l'ibis interactif est à l'écran)
const ThresholdIbis: React.FC = () => (
  <image className="gate-banner__ibis" href="/Mascotte/mascotte5.png" x="50" y="67" width="20" height="27" preserveAspectRatio="xMidYMax meet" />
);

// Copte — « Le monastère » (15b) : Wadi Natroun, murs blanchis, coupoles,
// croix copte, porte en plein cintre à trois gradins et médaillon.
const MonastereGate: React.FC<GateProps> = ({ ibis = true }) => (
  <svg className="gate-banner__gate" width="120" height="96" viewBox="0 0 120 96">
    <rect x="4" y="52" width="112" height="44" fill="#EFE6D2" />
    <rect x="4" y="50" width="112" height="2.4" fill="#CDB68A" />
    {/* Petites coupoles et leurs croix */}
    <path d="M9 52 Q21 35 33 52 Z" fill="#E3D2AE" />
    <path d="M87 52 Q99 35 111 52 Z" fill="#E3D2AE" />
    {[21, 99].map(cx => (
      <React.Fragment key={cx}>
        <rect x={cx - 0.8} y="30" width="1.6" height="8" fill="#3D2314" />
        <rect x={cx - 3} y="32.4" width="6" height="1.6" fill="#3D2314" />
      </React.Fragment>
    ))}
    {/* Corps central, coupole et croix copte */}
    <path d="M34 96 L35 34 L85 34 L86 96 Z" fill="#EFE6D2" />
    <path d="M36.5 36 Q60 3 83.5 36 Z" fill="#E3D2AE" />
    <path d="M36.5 36 Q60 3 60 12 Q48 14 42 36 Z" fill="#00000014" />
    <rect x="34" y="34" width="52" height="2.6" fill="#CDB68A" />
    <path d="M58.6 2 L61.4 2 L60.72 5 L64 4.6 L64 7.4 L60.72 7 L61.4 10 L58.6 10 L59.28 7 L56 7.4 L56 4.6 L59.28 5 Z" fill="#3D2314" />
    {/* Fenêtres */}
    <path d="M16 74 L16 66 A3 3 0 0 1 22 66 L22 74 Z" fill="#241812" />
    <path d="M98 74 L98 66 A3 3 0 0 1 104 66 L104 74 Z" fill="#241812" />
    {/* Médaillon */}
    <circle cx="60" cy="46" r="4.6" fill="none" stroke="#C77B4A" strokeWidth="1.4" />
    <path d="M59.09 43.4 L60.91 43.4 L60.468 45.35 L62.6 45.09 L62.6 46.91 L60.468 46.65 L60.91 48.6 L59.09 48.6 L59.532 46.65 L57.4 46.91 L57.4 45.09 L59.532 45.35 Z" fill="#C77B4A" />
    {/* Porte en plein cintre à trois gradins */}
    <path d="M46.5 96 L46.5 67 A13.5 13.5 0 0 1 73.5 67 L73.5 96 Z" fill="#00000030" transform="translate(1.6 1.2)" />
    <path d="M46.5 96 L46.5 67 A13.5 13.5 0 0 1 73.5 67 L73.5 96 Z" fill="#C77B4A" />
    <path d="M50 96 L50 68 A10 10 0 0 1 70 68 L70 96 Z" fill="#8A4A25" />
    <path d="M53 96 L53 69 A7 7 0 0 1 67 69 L67 96 Z" fill="#241812" />
    <path d="M53 96 L53 69 A7 7 0 0 1 60 62 L60 64.4 A5 5 0 0 0 55.2 70 L55.2 96 Z" fill="#000" opacity="0.35" />
    {ibis && <ThresholdIbis />}
    <rect x="0" y="93" width="120" height="3" fill="#CDB68A" />
  </svg>
);

// Créneaux triangulaires du mur de corail (15e, 15f)
const Crenels: React.FC<{ xs: number[]; y: number }> = ({ xs, y }) => (
  <>
    {xs.map(x => <path key={x} d={`M${x} ${y} L${x + 3} ${y - 5} L${x + 6} ${y} Z`} fill="#EFE6D2" />)}
  </>
);

// Battants cloutés de laiton : une grille de clous par battant
const Studs: React.FC<{ cols: number[]; rows: number[] }> = ({ cols, rows }) => (
  <>
    {cols.flatMap(cx => rows.map(cy => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.1" fill="#D4925F" />))}
  </>
);

const range = (n: number, start: number, step: number) => Array.from({ length: n }, (_, i) => start + i * step);

// Lingala — porte rectangulaire (15e) : linteau sculpté de rosaces, montants
// à tresse, deux battants cloutés, fenêtres à claustra.
const LinteauGate: React.FC<GateProps> = ({ ibis = true }) => (
  <svg className="gate-banner__gate" width="120" height="96" viewBox="0 0 120 96">
    <rect x="0" y="58" width="120" height="38" fill="#EFE6D2" />
    <rect x="22" y="30" width="76" height="66" fill="#EFE6D2" />
    <Crenels xs={[0, 6, 12, 18, 96, 102, 108, 114]} y={58} />
    <Crenels xs={range(13, 22, 6)} y={30} />
    <rect x="22" y="30" width="76" height="1.4" fill="#CDB68A" />
    {/* Fenêtres à claustra */}
    {[8, 103].map(x => (
      <React.Fragment key={x}>
        <rect x={x} y="68" width="9" height="13" fill="#241812" />
        {[1.5, 4.1, 6.7].map(dx => <rect key={dx} x={x + dx} y="68" width="0.9" height="13" fill="#5A3A22" />)}
      </React.Fragment>
    ))}
    {/* Encadrement et linteau à rosaces */}
    <rect x="35.5" y="17" width="53" height="79" fill="#00000030" transform="translate(1.6 1.2)" />
    <rect x="35" y="17" width="50" height="79" fill="#3D2314" />
    <rect x="35" y="17" width="50" height="15" fill="#5A3A22" />
    {range(7, 40, 6.6).map(cx => <circle key={cx} cx={cx} cy="24.5" r="2.3" fill="none" stroke="#D4925F" strokeWidth="1" />)}
    <rect x="37" y="29" width="46" height="1" fill="#D4925F" opacity="0.7" />
    <rect x="37" y="19" width="46" height="1" fill="#D4925F" opacity="0.7" />
    {/* Montants à tresse */}
    <path d="M39 34 L39 94" stroke="#D4925F" strokeWidth="1.4" strokeDasharray="2 1.6" fill="none" />
    <path d="M81 34 L81 94" stroke="#D4925F" strokeWidth="1.4" strokeDasharray="2 1.6" fill="none" />
    {/* Battants */}
    <rect x="43" y="34" width="34" height="60" fill="#241812" />
    <rect x="59.4" y="34" width="1.2" height="60" fill="#5A3A22" />
    <rect x="43" y="34" width="3" height="60" fill="#000" opacity="0.35" />
    <Studs cols={[47, 51.5, 56, 64, 68.5, 73]} rows={range(7, 40, 8)} />
    <ellipse cx="60" cy="96" rx="10" ry="1.6" fill="#D4925F" opacity="0.6" />
    {ibis && <ThresholdIbis />}
    <rect x="0" y="93" width="120" height="3" fill="#CDB68A" />
  </svg>
);

// Rayons de l'imposte en éventail (15f) : de r = 13 à r = 23 autour de (60, 46)
const FAN_RAYS = Array.from({ length: 10 }, (_, i) => {
  const a = Math.PI - (i * Math.PI) / 9;
  const c = Math.cos(a), s = Math.sin(a);
  return { x1: 60 + 13 * c, y1: 46 - 13 * s, x2: 60 + 23 * c, y2: 46 - 23 * s };
});

// Swahili — « La porte arquée » (15f) : arc en plein cintre, imposte en
// éventail sculpté, battants cloutés, montants sur bases en lotus.
const ArqueeGate: React.FC<GateProps> = ({ ibis = true }) => (
  <svg className="gate-banner__gate" width="120" height="96" viewBox="0 0 120 96">
    <rect x="0" y="58" width="120" height="38" fill="#EFE6D2" />
    <rect x="20" y="34" width="80" height="62" fill="#EFE6D2" />
    <Crenels xs={[0, 6, 12, 98, 104, 110]} y={58} />
    <Crenels xs={range(13, 20, 6)} y={34} />
    <rect x="9" y="68" width="8" height="12" fill="#241812" />
    <rect x="103" y="68" width="8" height="12" fill="#241812" />
    {/* Arc et imposte en éventail */}
    <path d="M31 96 L31 46 A29 29 0 0 1 89 46 L89 96 Z" fill="#00000030" transform="translate(1.6 1.2)" />
    <path d="M31 96 L31 46 A29 29 0 0 1 89 46 L89 96 Z" fill="#3D2314" />
    <path d="M36 46 A24 24 0 0 1 84 46 L73 46 A13 13 0 0 0 47 46 Z" fill="#5A3A22" />
    {FAN_RAYS.map((r, i) => <line key={i} {...r} stroke="#D4925F" strokeWidth="1.1" />)}
    <path d="M47 46 A13 13 0 0 1 73 46 Z" fill="#241812" />
    {range(5, 49.5, 5.25).map(x => <rect key={x} x={x} y="34" width="0.9" height="12" fill="#5A3A22" opacity="0.9" />)}
    {/* Battants */}
    <rect x="36" y="46" width="48" height="1.6" fill="#D4925F" />
    <rect x="41" y="48" width="38" height="46" fill="#241812" />
    <rect x="59.4" y="48" width="1.2" height="46" fill="#5A3A22" />
    <rect x="41" y="48" width="3" height="46" fill="#000" opacity="0.35" />
    <Studs cols={[45, 50, 55, 65, 70, 75]} rows={range(5, 54, 8.5)} />
    {/* Montants sur bases en lotus */}
    <path d="M33.5 94 L33.5 50" stroke="#D4925F" strokeWidth="1.2" strokeDasharray="1.6 1.6" fill="none" />
    <path d="M86.5 94 L86.5 50" stroke="#D4925F" strokeWidth="1.2" strokeDasharray="1.6 1.6" fill="none" />
    <ellipse cx="33.5" cy="93" rx="3" ry="2" fill="#D4925F" />
    <ellipse cx="86.5" cy="93" rx="3" ry="2" fill="#D4925F" />
    {ibis && <ThresholdIbis />}
    <rect x="0" y="93" width="120" height="3" fill="#CDB68A" />
  </svg>
);

// Kama — « Le pylône de Karnak » (16a) : deux môles inclinés à corniche en
// gorge, colonnes de hiéroglyphes et ânkh ; porte centrale sous disque ailé,
// flanquée de deux obélisques dorés.
const GORGE_RIBS_L = range(10, 13, 3.6);
const GORGE_RIBS_R = range(10, 71, 3.6);
const GORGE_RIBS_C = range(7, 47, 3.6);

const Ankh: React.FC<{ cx: number }> = ({ cx }) => (
  <>
    <ellipse cx={cx} cy="52.04" rx="2.34" ry="3.06" fill="none" stroke="#8A4A25" strokeWidth="1.62" />
    <rect x={cx - 3.96} y="55.1" width="7.92" height="1.62" fill="#8A4A25" />
    <rect x={cx - 0.81} y="55.1" width="1.62" height="7.2" fill="#8A4A25" />
  </>
);

// Colonne de hiéroglyphes : deux filets, signes alternés trait / disque
const GlyphColumn: React.FC<{ x: number }> = ({ x }) => (
  <>
    <rect x={x} y="38" width="1.2" height="34" fill="#8A4A25" opacity="0.6" />
    <rect x={x + 5} y="38" width="1.2" height="34" fill="#8A4A25" opacity="0.6" />
    <rect x={x + 2} y="40.4" width="2.2" height="1.3" fill="#8A4A25" />
    <circle cx={x + 3.1} cy="49.48" r="1.1" fill="#8A4A25" />
    <rect x={x + 2} y="57.37" width="2.2" height="1.3" fill="#8A4A25" />
    <circle cx={x + 3.1} cy="66.45" r="1.1" fill="#8A4A25" />
  </>
);

const KarnakGate: React.FC<GateProps> = ({ ibis = true }) => (
  <svg className="gate-banner__gate" width="120" height="96" viewBox="0 0 120 96">
    {/* Môles inclinés et corniches en gorge */}
    <path d="M4 96 L12 22 L50 22 L54 96 Z" fill="#D9B77E" />
    <path d="M66 96 L70 22 L108 22 L116 96 Z" fill="#D9B77E" />
    <path d="M10 22 L52 22 L54 16 Q31 13 8 16 Z" fill="#CDB68A" />
    <rect x="11" y="22" width="40" height="2.2" fill="#8A4A25" />
    {GORGE_RIBS_L.map(x => <rect key={x} x={x} y="16" width="1.2" height="5" fill="#8A4A25" opacity="0.35" />)}
    <path d="M68 22 L110 22 L112 16 Q89 13 66 16 Z" fill="#CDB68A" />
    <rect x="69" y="22" width="40" height="2.2" fill="#8A4A25" />
    {GORGE_RIBS_R.map(x => <rect key={x} x={x} y="16" width="1.2" height="5" fill="#8A4A25" opacity="0.35" />)}
    <path d="M12 22 L4 96" stroke="#8A4A25" strokeWidth="1.3" fill="none" />
    <path d="M108 22 L116 96" stroke="#8A4A25" strokeWidth="1.3" fill="none" />
    {/* Registres, hiéroglyphes et ânkh */}
    {[34, 54, 74].map(y => (
      <React.Fragment key={y}>
        <rect x="16" y={y} width="32" height="1" fill="#8A4A25" opacity="0.35" />
        <rect x="72" y={y} width="32" height="1" fill="#8A4A25" opacity="0.35" />
      </React.Fragment>
    ))}
    <GlyphColumn x={22} />
    <GlyphColumn x={92} />
    <Ankh cx={36} />
    <Ankh cx={84} />
    {/* Porte centrale sous disque ailé */}
    <rect x="46" y="40" width="28" height="56" fill="#D9B77E" />
    <path d="M44 40 L76 40 L78 34 Q60 31 42 34 Z" fill="#CDB68A" />
    <rect x="45" y="40" width="30" height="2.2" fill="#8A4A25" />
    {GORGE_RIBS_C.map(x => <rect key={x} x={x} y="34" width="1.2" height="5" fill="#8A4A25" opacity="0.35" />)}
    <circle cx="60" cy="36" r="2.16" fill="#C77B4A" />
    <path d="M57.84 36 Q53.7 33.84 49.2 36.18 Q53.7 36.54 57.84 37.08 Z" fill="#8A4A25" />
    <path d="M62.16 36 Q66.3 33.84 70.8 36.18 Q66.3 36.54 62.16 37.08 Z" fill="#8A4A25" />
    <rect x="47" y="47" width="26" height="49" fill="#D4925F" />
    <rect x="49.5" y="49.5" width="21" height="46.5" fill="#8A4A25" />
    <rect x="52" y="52" width="16" height="44" fill="#241812" />
    <path d="M52 96 L52 52 L68 52 L68 54.4 L54.4 54.4 L54.4 96 Z" fill="#000" opacity="0.35" />
    {ibis && <ThresholdIbis />}
    {/* Obélisques dorés */}
    <path d="M1 96 L2 38 L4 34 L6 38 L7 96 Z" fill="#C9A046" />
    <path d="M113 96 L114 38 L116 34 L118 38 L119 96 Z" fill="#C9A046" />
    <rect x="0" y="93" width="120" height="3" fill="#8A4A25" />
  </svg>
);

// La porte de chaque langue, partagée avec le sélecteur de langue (Tour 17)
const GATES: Record<LanguageCode, React.FC<GateProps>> = {
  ba: SegouGate, ff: SegouGate, wf: SegouGate,
  co: MonastereGate, la: LinteauGate, sw: ArqueeGate, ka: KarnakGate,
  yo: LinteauGate,
};

export const LanguageGate: React.FC<{ lang: LanguageCode; ibis?: boolean }> = ({ lang, ibis }) => {
  const Gate = GATES[lang];
  return <Gate ibis={ibis} />;
};

// Façade générique : murs latéraux (travées répétées, rognées sur le bord
// extérieur) de part et d'autre de la porte. Le mur est porté par le côté,
// les travées ne portent que l'ornement (croix, claustra).
const BAYS = 10;

const GateBanner: React.FC<{ variant: string; wall: string; ornament: string; gate: React.ReactNode }> = ({ variant, wall, ornament, gate }) => {
  const bays = Array.from({ length: BAYS }, (_, i) => (
    <span key={i} className="gate-banner__bay"><span className={ornament} /></span>
  ));
  return (
    <div className={`gate-banner gate-banner--${variant}`}>
      <div className="gate-banner__facade">
        <div className={`gate-banner__side gate-banner__side--left ${wall}`}>{bays}</div>
        {gate}
        <div className={`gate-banner__side ${wall}`}>{bays}</div>
      </div>
      <div className="gate-banner__base" />
    </div>
  );
};

// Largeur de la barre de défilement exposée au CSS (--scrollbar-w) : le
// bandeau s'étend à toute la largeur visible de l'écran (voir TribalBorder.css).
function useScrollbarWidthVar() {
  useLayoutEffect(() => {
    const set = () => {
      const w = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.documentElement.style.setProperty('--scrollbar-w', `${w}px`);
    };
    set();
    window.addEventListener('resize', set);
    return () => window.removeEventListener('resize', set);
  }, []);
}

const TribalBorder: React.FC = () => {
  useScrollbarWidthVar();
  return (
  <div className="tribal-border" aria-hidden="true">
    <div className="segou">
      <div className="segou__facade">
        <div className="segou__side segou__side--left">
          {Array.from({ length: SIDE_UNITS }, (_, i) => <Wall key={i} />)}
        </div>
        <SegouGate />
        <div className="segou__side segou__side--right">
          {Array.from({ length: SIDE_UNITS }, (_, i) => <Wall key={i} />)}
          <span className="segou__pilaster" />
        </div>
      </div>
      <div className="segou__base" />
    </div>
    <GateBanner variant="co" wall="wall--monastere" ornament="orn--cross" gate={<MonastereGate />} />
    <GateBanner variant="la" wall="wall--corail" ornament="orn--claustra" gate={<LinteauGate />} />
    <GateBanner variant="sw" wall="wall--corail" ornament="orn--claustra" gate={<ArqueeGate />} />
    <GateBanner variant="ka" wall="wall--gres" ornament="orn--glyph" gate={<KarnakGate />} />
  </div>
  );
};

export default TribalBorder;
